import { useCallback, useMemo, useState } from 'react';
import {
  FollowStatusesEnum,
  type UserSportsInsertInput,
  type VenueFollowsInsertInput,
  useDeactivatePreferredVenuesMutation,
  useDeletePlayerGalleryPhotosMutation,
  useGetPlayerFinderProfileQuery,
  useGetPlayerFinderRatingScalesQuery,
  useInsertPlayerGalleryPhotosMutation,
  useSavePlayerFinderAvailabilityMutation,
  useSavePlayerRatingScalesMutation,
  useSavePlayerSportsMutation,
  useSavePreferredVenuesMutation,
  useUpdatePlayerUserMutation,
} from 'types/generated/client';
import type {
  PlayingLevelValues,
  PreferredCourtsValues,
  ProfileBasicsValues,
  UploadedImage,
} from 'utils/shared/playerFinder';
import { useViewer } from 'hooks/useViewer';
import {
  buildAvailabilityRows,
  buildGalleryFileInserts,
  buildRatingScaleIdMaps,
  buildRatingScaleInserts,
  buildSkillLevelSet,
  droppedIds,
  toPlayingLevelValues,
  toPreferredCourtsValues,
  toProfileBasicsValues,
  toProfilePhotos,
} from 'screens/FindOnboarding/utils';

const SAVE_ERROR = "We couldn't save that. Please try again.";

interface Params {
  /** Only the owner can edit, so the hydration query stays off for visitors. */
  isViewerProfile: boolean;
  /** Refetches the profile so the page reflects the edit straight away. */
  onSaved: () => void;
}

/**
 * Backs the profile's in-place editors. Loads the player's finder record once and
 * reuses the onboarding's form/DB mappers, so a field saved here and the same
 * field saved in the questionnaire write identical rows.
 *
 * Saves are awaited (unlike onboarding's fire-and-forget) because a modal has to
 * know whether to show a spinner, surface a failure, or close.
 */
export function usePlayerProfileEditor({ isViewerProfile, onSaved }: Params) {
  const { userId } = useViewer();
  const isEnabled = isViewerProfile && !!userId;

  const [updateUser] = useUpdatePlayerUserMutation();
  const [saveSports] = useSavePlayerSportsMutation();
  const [saveRatingScales] = useSavePlayerRatingScalesMutation();
  const [saveAvailability] = useSavePlayerFinderAvailabilityMutation();
  const [savePreferredVenues] = useSavePreferredVenuesMutation();
  const [deactivatePreferredVenues] = useDeactivatePreferredVenuesMutation();
  const [insertGalleryPhotos] = useInsertPlayerGalleryPhotosMutation();
  const [deleteGalleryPhotos] = useDeletePlayerGalleryPhotosMutation();

  const { data: ratingScalesData } = useGetPlayerFinderRatingScalesQuery({ skip: !isEnabled });
  const { data, refetch } = useGetPlayerFinderProfileQuery({
    variables: { id: userId ?? '' },
    skip: !isEnabled,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const values = useMemo(
    () => ({
      profileBasics: data ? toProfileBasicsValues(data) : null,
      photos: data ? toProfilePhotos(data) : null,
      playingLevel: data ? toPlayingLevelValues(data) : null,
      preferredCourts: data ? toPreferredCourtsValues(data) : null,
    }),
    [data],
  );

  /** Runs the writes, then refreshes both the editor's copy and the visible profile. */
  const run = useCallback(
    async (writes: () => Promise<unknown>) => {
      if (!userId) return false;
      setIsSaving(true);
      setError(null);
      try {
        await writes();
        // Different queries against different roots — neither reads the other.
        await Promise.all([refetch(), onSaved()]);
        return true;
      } catch {
        setError(SAVE_ERROR);
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [userId, refetch, onSaved],
  );

  /**
   * Editing in place must be able to *clear* a field, so these write the form's
   * value verbatim — unlike onboarding's additive `buildProfileBasicsSet`, which
   * deliberately skips empties so a skipped step can't blank existing data.
   */
  const saveAboutMe = useCallback(
    (next: Pick<ProfileBasicsValues, 'aboutMe' | 'gender' | 'birthday' | 'birthdayVisibility'>) =>
      run(() =>
        updateUser({
          variables: {
            id: userId as string,
            set: {
              aboutMe: next.aboutMe.trim() || null,
              gender: next.gender,
              birthday: next.birthday || null,
              birthdayVisibility: next.birthdayVisibility,
            },
          },
        }),
      ),
    [run, updateUser, userId],
  );

  const saveSocialLinks = useCallback(
    (socialLinks: ProfileBasicsValues['socialLinks']) =>
      run(() =>
        updateUser({
          variables: {
            id: userId as string,
            // Empty string, not null: these columns default to '' and tiktok_url
            // is NOT NULL, so clearing a row with null would fail the write.
            set: {
              instagramUrl: socialLinks.instagram.trim(),
              facebookUrl: socialLinks.facebook.trim(),
              tiktokUrl: socialLinks.tiktok.trim(),
              linkedinUrl: socialLinks.linkedin.trim(),
              xUrl: socialLinks.x.trim(),
            },
          },
        }),
      ),
    [run, updateUser, userId],
  );

  const saveGalleryPhotos = useCallback(
    (photos: UploadedImage[]) =>
      run(async () => {
        // Rows without an id are this session's uploads; the rest already exist.
        const files = buildGalleryFileInserts(photos, userId as string);
        const removedIds = droppedIds(
          values.photos?.additionalPhotos ?? [],
          photos,
          (photo) => photo.id,
        );

        await Promise.all([
          files.length ? insertGalleryPhotos({ variables: { files } }) : null,
          removedIds.length ? deleteGalleryPhotos({ variables: { ids: removedIds } }) : null,
        ]);
      }),
    [run, insertGalleryPhotos, deleteGalleryPhotos, values.photos, userId],
  );

  const savePlayingLevel = useCallback(
    (next: PlayingLevelValues) =>
      run(async () => {
        const id = userId as string;
        const sports: UserSportsInsertInput[] = next.sports.map((sport) => ({
          userId: id,
          sport,
        }));
        const skillLevels = buildSkillLevelSet(next);
        const maps = buildRatingScaleIdMaps(ratingScalesData);
        const { pickleball, tennis, padel } = buildRatingScaleInserts(next, id, maps);

        // Three disjoint tables; none reads another's result.
        await Promise.all([
          saveSports({ variables: { userId: id, sports } }),
          Object.keys(skillLevels).length
            ? updateUser({ variables: { id, set: skillLevels } })
            : null,
          pickleball.length || tennis.length || padel.length
            ? saveRatingScales({ variables: { pickleball, tennis, padel } })
            : null,
        ]);
      }),
    [run, saveSports, updateUser, saveRatingScales, ratingScalesData, userId],
  );

  const saveAvailabilityWindows = useCallback(
    (next: PreferredCourtsValues) =>
      run(async () => {
        const id = userId as string;
        await saveAvailability({
          variables: { userId: id, windows: buildAvailabilityRows(next, id) },
        });
      }),
    [run, saveAvailability, userId],
  );

  const savePreferredVenuesList = useCallback(
    (next: PreferredCourtsValues) =>
      run(async () => {
        const id = userId as string;

        // Preferred courts reuse venue_follows (ACTIVE); availability is finder-owned.
        const venues: VenueFollowsInsertInput[] = next.venues.map((venue) => ({
          userId: id,
          venueId: venue.id,
          status: FollowStatusesEnum.Active,
        }));
        const removedIds = droppedIds(
          values.preferredCourts?.venues ?? [],
          next.venues,
          (venue) => venue.id,
        );

        await Promise.all([
          venues.length ? savePreferredVenues({ variables: { venues } }) : null,
          removedIds.length
            ? deactivatePreferredVenues({ variables: { userId: id, venueIds: removedIds } })
            : null,
        ]);
      }),
    [run, savePreferredVenues, deactivatePreferredVenues, values.preferredCourts, userId],
  );

  return {
    isSaving,
    error,
    clearError: () => setError(null),
    values,
    saveAboutMe,
    saveSocialLinks,
    saveGalleryPhotos,
    savePlayingLevel,
    saveAvailabilityWindows,
    savePreferredVenuesList,
  };
}
